import { Space } from 'antd'
import { useEffect, useRef } from 'react'
import { useGlobalState } from '../../../../../../../../../context/GlobalStateContext'
import {
  CSVImportLatestJobResponse,
  CSVImportResultJSON,
  JobStatus,
} from 'shared'
import ResultsSummary from './components/ResultsSummary'
import ResultsTable from './components/ResultsTable'
import { errorHandler } from '../../../../../../../../../hooks/errorHandler'
import { useCIMSModal } from '../../../../../../../../../components/CIMSModal/hooks/useCIMSModal'
import CIMSButton from '../../../../../../../../../components/CIMSButton'
import ImportModal from './components/ImportModal'
import { useFetch } from '../../../../../../../../../hooks/useFetch'
import {
  simpleErrorNotification,
  simpleInfoNotification,
  simpleSuccessNotification,
} from '../../../../../../../../../hooks/notificationHandler'
import EmptyCSV from './components/EmptyCSV'

// Interval in milliseconds for polling progress updates.
const POLL_INTERVAL = 2000

interface CSVImportResultJSONWithProgress extends CSVImportResultJSON {
  progress: number
}

function Imports() {
  const {
    state: { projectGlobalId: projectId },
  } = useGlobalState()

  const timerId = useRef<number>()
  // This hook is defined and used directly within this component, rather than
  // being placed in a provider like most of our components, because it relies
  // on polling functionality that needs access to the timer id.
  const { data, loading, reload } = useFetch<CSVImportLatestJobResponse>({
    url: `/api/projects/${projectId?.toUpperCase()}/csv/imports/latest`,
    skip: !projectId,
    onError: (e) => {
      stopPolling()
      errorHandler(e)
    },
  })
  const isInitialLoad =
    (loading && data === undefined) ||
    (loading && data?.job?.statusId === JobStatus.Pending.id)
  const isJobProcessing =
    data?.job?.statusId === JobStatus.Pending.id ||
    data?.job?.statusId === JobStatus.Processing.id
  const isJobComplete = data?.job?.statusId === JobStatus.Completed.id
  const isJobFailed = data?.job?.statusId === JobStatus.Failed.id
  const [visible, toggleModal] = useCIMSModal()
  const notificationsEnabled = useRef(false)

  function stopPolling() {
    clearTimeout(timerId.current)
  }

  useEffect(() => {
    if (!projectId) {
      return errorHandler('Project id must be defined')
    }
    /**
     * Perform simple polling of the results. Initially this was done with
     * server-sent events, but that attempt introduced too much complexity and
     * KISS won out.
     */
    function poll() {
      reload()
      timerId.current = window.setTimeout(() => {
        poll()
      }, POLL_INTERVAL)
    }

    if (isJobProcessing) {
      notificationsEnabled.current = true
      poll()
    }

    if (notificationsEnabled.current && isJobComplete) {
      notificationsEnabled.current = false
      simpleSuccessNotification('Import complete')
    }

    if (notificationsEnabled.current && isJobFailed) {
      notificationsEnabled.current = false
      simpleErrorNotification('Import failed')
    }

    return () => {
      stopPolling()
    }
    // Including the whole data object will result in an infinite loop. Instead,
    // only react to changes in status.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.job?.statusId, projectId, reload])

  const stats = (data?.results ?? []).reduce<CSVImportResultJSONWithProgress>(
    (prev, curr) => {
      if (!data?.job?.input?.rowCount) return prev
      prev.created += curr.created
      prev.edited += curr.edited
      prev.errors += curr.errors
      prev.progress = Math.round(
        ((prev.created + prev.edited + prev.errors) /
          (data.job.input.rowCount - 1)) *
          100
      )
      prev.rows = [...prev.rows, ...curr.rows]
      return prev
    },
    { created: 0, edited: 0, errors: 0, progress: 0, rows: [] }
  )

  const button = (
    <CIMSButton
      loading={isJobProcessing || isInitialLoad}
      disabled={[
        {
          disabled: isJobProcessing,
          reason: { why: 'disabled', message: 'Import is processing' },
        },
      ]}
      type="primary"
      onClick={() => toggleModal(true)}
    >
      Import CSV
    </CIMSButton>
  )

  return (
    <Space
      direction="vertical"
      size="large"
      style={{ maxWidth: 800, display: 'flex', paddingBottom: '48px' }}
    >
      {data?.job !== null ? button : <EmptyCSV>{button}</EmptyCSV>}
      <ImportModal
        visible={visible}
        onCancel={() => toggleModal(false)}
        onOk={() => {
          toggleModal(false)
          simpleInfoNotification(
            `We're processing your CSV in the background! You can safely leave this page.`
          )
          reload()
        }}
      />
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {!!data?.job?.id && (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <ResultsSummary
              job={data.job}
              title={data.job.input?.filename}
              {...stats}
            />
          </Space>
        )}
        {!!data?.job?.id && (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <ResultsTable
              loading={
                isInitialLoad || (isJobProcessing && data.results.length === 0)
              }
              rows={stats.rows}
            />
          </Space>
        )}
      </Space>
    </Space>
  )
}

export default Imports
